import { operation, requestOperation, type ObservedOperation, type Outcome } from '../shared/observability.js';
import { observationHeaders } from './observation-context.js';
import { reportBrowserError } from './error-tracking.js';
import { observe } from './telemetry.js';

export class ApiOfflineError extends Error { constructor(options?: ErrorOptions) { super('The server could not be reached.', options); this.name = 'ApiOfflineError'; } }
export function errorOutcome(error: unknown): Outcome {
  if (error instanceof ApiOfflineError) return 'offline';
  if (error instanceof Error && error.name === 'AbortError') return 'cancelled';
  if (error && typeof error === 'object' && 'status' in error) return Number(error.status) >= 500 ? 'failed' : 'rejected';
  return 'failed';
}
export function reportUnexpected(error: unknown, fields: Record<string, unknown> = {}) {
  if (error instanceof ApiOfflineError || error instanceof Error && error.name === 'AbortError' || error && typeof error === 'object' && 'status' in error) return;
  reportBrowserError(error, { kind: 'handled', ...fields });
}

export async function observedRequest<T>(method: string, path: string, fetcher: typeof fetch, init: RequestInit,
  decode: (response: Response) => Promise<T>, existing?: ObservedOperation): Promise<T> {
  const name = requestOperation(method, path);
  const op = existing ?? (name ? operation(observe, name) : undefined);
  op?.attempt();
  try {
    let response: Response;
    try { response = await fetcher(path, { ...init, method, headers: { ...init.headers, ...observationHeaders(op?.id) } }); }
    catch (error) { if (error instanceof Error && error.name === 'AbortError') throw error; throw new ApiOfflineError({ cause: error }); }
    if (op) Object.assign(op.context, { requestId: response.headers.get('x-request-id') ?? undefined, serverBuildId: response.headers.get('x-build-id') ?? undefined, statusCode: response.status });
    const result = await decode(response);
    if (!existing) op?.finish('success');
    return result;
  } catch (error) {
    reportUnexpected(error, op?.context);
    if (!existing) op?.finish(errorOutcome(error));
    throw error;
  }
}
